import { ref, unref } from 'vue';
import SalesTransactionDocumentsAPI from 'dashboard/api/salesTransactionDocuments';
import SalesTransactionPartiesAPI from 'dashboard/api/salesTransactionParties';
import SalesTransactionPartyDocumentsAPI from 'dashboard/api/salesTransactionPartyDocuments';

export function useNucleoWorkOrderServiceDocuments({
  transactionId,
  t,
  notify,
}) {
  const partyTypes = ref([]);
  const propertyTypes = ref([]);
  const loading = ref(false);
  const uploading = ref(false);

  const load = async () => {
    if (!unref(transactionId)) return;

    loading.value = true;
    try {
      const [partyResponse, propertyResponse] = await Promise.all([
        SalesTransactionPartiesAPI.catalog(unref(transactionId)),
        SalesTransactionDocumentsAPI.catalog(unref(transactionId)),
      ]);
      partyTypes.value = partyResponse?.data?.data?.tipodocumentos || [];
      propertyTypes.value = propertyResponse?.data?.data || [];
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.LOAD_ERROR')
      );
    } finally {
      loading.value = false;
    }
  };

  const typesFor = (documentTypes, ids, options) => {
    if (documentTypes.length) return documentTypes;

    return options.filter(option => ids.includes(String(option.id)));
  };

  const uploadParty = async ({
    partyId,
    files,
    typeIds,
    documentTypes = [],
  }) => {
    if (!partyId || !files.length) return [];

    uploading.value = true;
    try {
      const types = typesFor(documentTypes, typeIds, partyTypes.value);
      const responses = await Promise.all(
        files.map(file =>
          SalesTransactionPartyDocumentsAPI.upload(
            unref(transactionId),
            partyId,
            file,
            undefined,
            types
          )
        )
      );
      return responses
        .map(response => response?.data?.data?.id)
        .filter(Boolean);
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.UPLOAD_ERROR')
      );
      return [];
    } finally {
      uploading.value = false;
    }
  };

  const uploadProperty = async ({ files, typeIds, documentTypes = [] }) => {
    if (!files.length) return [];

    uploading.value = true;
    try {
      const types = typesFor(documentTypes, typeIds, propertyTypes.value);
      const responses = await Promise.all(
        files.map(file =>
          SalesTransactionDocumentsAPI.upload(unref(transactionId), file)
        )
      );
      const documents = responses
        .map(response => response?.data?.data)
        .filter(Boolean);
      if (types.length) {
        await Promise.all(
          documents.map(document =>
            SalesTransactionDocumentsAPI.update(
              unref(transactionId),
              document.id,
              types
            )
          )
        );
      }
      return documents.map(document => document.id);
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.UPLOAD_ERROR')
      );
      return [];
    } finally {
      uploading.value = false;
    }
  };

  return {
    load,
    loading,
    partyTypes,
    propertyTypes,
    uploadParty,
    uploadProperty,
    uploading,
  };
}
